import { useCallback, useEffect, useRef, useState } from 'react'
import { listMembers, type Member } from '../data/members'

export type MembersState = { phase: 'loading' } | { phase: 'ready'; members: Member[] } | { phase: 'failed' }

/**
 * The active company's current members (GRO-106 design, "Company members in the shell").
 * App owns them, because the sidebar count and the user menu's Owner count need them
 * before the Members page opens.
 *
 * Each load takes a new activation number, and a company switch takes one at once and goes
 * back to `loading`; an answer is applied only while its activation is current, so company
 * A's list never reaches company B. A `404` reports to `onAccessLost`; a `401` has already
 * been reported by `data/members.ts`. Disabled, for a guest (GRO-83), it neither reads nor
 * listens for focus.
 */
export function useCompanyMembers(companyId: string, onAccessLost?: (companyId: string) => void, enabled = true): {
  state: MembersState
  reload: () => void
} {
  const [loaded, setLoaded] = useState<{ companyId: string; state: MembersState }>({ companyId, state: { phase: 'loading' } })
  const activation = useRef(0)
  const lost = useRef(onAccessLost)
  lost.current = onAccessLost

  const load = useCallback((keep: boolean) => {
    const mine = ++activation.current
    if (!keep) setLoaded({ companyId, state: { phase: 'loading' } })
    void listMembers(companyId).then((result) => {
      if (mine !== activation.current) return
      if (result.status === 'ok') setLoaded({ companyId, state: { phase: 'ready', members: result.body } })
      else if (result.status === 'stale' || result.status === 'expired') return
      else {
        setLoaded({ companyId, state: { phase: 'failed' } })
        if (result.status === 'notFound') lost.current?.(companyId)
      }
    })
  }, [companyId])

  useEffect(() => {
    if (!enabled) return
    load(false)
    const onFocus = () => load(true)
    window.addEventListener('focus', onFocus)
    return () => {
      window.removeEventListener('focus', onFocus)
      activation.current += 1
    }
  }, [load, enabled])

  // A reload keeps the list on screen while it reads, so a refresh after a mutation does
  // not blank the table; a company switch never shows the previous company's rows.
  const reload = useCallback(() => load(true), [load])
  return { state: loaded.companyId === companyId ? loaded.state : { phase: 'loading' }, reload }
}
